import { memo, useReducer } from 'react';

import { Trash2, SquarePen } from 'lucide-react';
import { isEmpty, omit } from 'remeda';
import { toast } from 'sonner';
import useSWRMutation from 'swr/mutation';

import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';

import { SlideForm } from '../../form';
import { SlideFormDialog } from '../../slideFormDialog/SlideFormDialog';

import css from './MainScreenSlideCard.module.css';

import { mainScreenSettingsService } from 'v2/api/mainScreenSettingsService';
import { Popconfirm } from 'v2/components/popconfirm/Popconfirm';
import { SlideModel } from 'v2/types/SlideModel';
import { fileSrc, handleHttpError } from 'v2/utils';

interface MainScreenSlideCardProps {
  slide: SlideModel;
  onRevalidate: VoidFunction;
}

export const MainScreenSlideCard = memo<MainScreenSlideCardProps>(({ slide, onRevalidate }) => {
  const [isOpen, setOpen] = useReducer((state) => !state, false);

  const { trigger: triggerDelete } = useSWRMutation(['deleteSlide', slide.id], mainScreenSettingsService.deleteSlide, {
    onSuccess: () => {
      onRevalidate();
    },
    onError: handleHttpError,
  });

  const { trigger: triggerEdit, isMutating } = useSWRMutation(
    ['updateSlide', slide.id],
    mainScreenSettingsService.updateSlide,
    {
      onSuccess: () => {
        onRevalidate();
        toast.success('The slide has been successfully updated');
        setOpen();
      },
      onError: handleHttpError,
    }
  );

  const handleSubmit = (values: SlideForm, resetForm: VoidFunction) => {
    triggerEdit(values).then(() => {
      resetForm();
    });
  };

  return (
    <div className={css.mainScreenSlideCard}>
      <div
        style={{ backgroundImage: `url("${fileSrc(slide.backgroundImageId)}")` }}
        className={css.mainScreenSlideImgContainer}
      >
        <Chip className={css.mainScreenSlideCardChip} color="secondary" label={`#${slide.order}`} size="small" />

        <img className={css.mainScreenSlideCardButton} src={fileSrc(slide.buttonImageId)} alt={slide.buttonText} />
      </div>

      <div className={css.mainScreenSlideCardContent}>
        <div className={css.mainScreenSlideCardContentInner}>
          <div className={css.mainScreenSlideCardTitleContainer}>
            <Chip
              color={slide.isActive ? 'success' : 'default'}
              label={slide.isActive ? 'Active' : 'Inactive'}
              size="small"
            />

            <p className={css.mainScreenSlideTitle}>{slide.title}</p>
          </div>

          <p className={css.mainScreenSlideDesktop}>{slide.description}</p>

          {!isEmpty(slide.buttonText) && (
            <Button variant="outlined" disabled>
              {slide.buttonText}
            </Button>
          )}
        </div>

        <div className={css.mainScreenSlideActions}>
          <Popconfirm message="Are you sure that you want to delete this presentation slide?" onConfirm={triggerDelete}>
            <Button startIcon={<Trash2 width={18} />} variant="outlined" color="error" fullWidth>
              Delete
            </Button>
          </Popconfirm>

          <Button onClick={setOpen} startIcon={<SquarePen width={18} />} variant="outlined" color="warning">
            Edit
          </Button>

          <SlideFormDialog
            title="Edit Slide"
            isOpen={isOpen}
            onClose={setOpen}
            initialValues={omit(slide, ['id'])}
            isSubmitting={isMutating}
            onSubmit={handleSubmit}
          />
        </div>
      </div>
    </div>
  );
});
